import type {
  ConnectorExecutionDto,
  ConnectorRow,
  ConnectorSourceDetail,
  ContactField,
  UploadType,
  UploadTypeConfigurationDto,
  UploadTypeRegistryEntry,
} from '../models/dataConnector.types'

/** Shared with the label layer (ORP-18861) as the fallback for a field carrying no i18n key. */
export function words(value: string): string {
  return value
    .replace(/^upload_?/u, '')
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

function contactFields(dto: UploadTypeConfigurationDto): string[] {
  const raw = Array.isArray(dto.fields) ? dto.fields : (dto.fields?.contact_info ?? [])
  return raw.filter((field) => field !== 'action_source')
}

export function mergeUploadTypes(
  dtos: UploadTypeConfigurationDto[],
  registry: Readonly<Record<string, UploadTypeRegistryEntry>>,
  labelFor: (uploadType: string) => string = words,
): UploadType[] {
  return dtos.map((dto) => ({
    id: dto.upload_type,
    platform: dto.platform,
    label: labelFor(dto.upload_type),
    fields: contactFields(dto).map<ContactField>((field) => ({
      field,
      header: field === 'maid' ? 'Mobile Ad ID' : words(field),
    })),
    requiredFields: dto.required_fields ?? [],
    ...(registry[dto.upload_type] ?? {}),
  }))
}

export function executionRows(
  executions: ConnectorExecutionDto[],
  uploadTypes: UploadType[],
): ConnectorRow[] {
  return executions.map((execution) => {
    const type = uploadTypes.find((candidate) => candidate.id === execution.upload_type)
    const info = execution.additional_info
    // D3 — the sheet URL is carried verbatim. Never route it through `words()`, and never
    // pre-join it into a label string: `ConnectorSourceCell` owns the presentation.
    const detail: ConnectorSourceDetail =
      info.source === 'sheet'
        ? { kind: 'sheet', sheetUrl: String(info.sheet_id ?? '') }
        : {
            kind: 'bigquery',
            projectId: String(info.project_id ?? ''),
            datasetId: String(info.dataset_id ?? ''),
            tableId: String(info.table_id ?? ''),
          }
    return {
      id: execution.id,
      uploadType: type?.label ?? words(execution.upload_type),
      uploadTypeId: execution.upload_type,
      platform: type?.platform ?? 'unknown',
      frequency: words(String(info.frequency ?? '')),
      source: words(info.source),
      detail,
      unavailable: type === undefined,
      execution,
    }
  })
}

export function requiredContactFields(type: UploadType, actionSource: unknown): string[] {
  if (Array.isArray(type.requiredFields)) return type.requiredFields
  const key = actionSource === 'website' ? 'web' : String(actionSource ?? '')
  return type.requiredFields[key] ?? []
}
